<template>
  <component
    :is="isMockLink ? 'span' : 'a'"
    v-if="shouldShowLink"
    :href="linkHref"
    class="pev-Link"
    data-testid="platform-element-link"
    target="_blank"
    @click="onLinkClick"
  >
    <AppIcon height="24" icon-name="shortcut-next" width="24" />
    <slot />
  </component>
</template>

<script setup>
import { computed, inject } from 'vue'
import { useStore } from 'vuex'

import { AZURE_DEVOPS_SDK } from '@/utils/azure-devops-helpers/general'
import { useExternalLinkHelper } from '@/utils/external-link-composables'
import { EXTERNAL_LINK_HANDLER_SOURCES } from '@/utils/external-link-helper'
import { isFirefox } from '@/utils/general'
import {
  appPlatformInjectionKey,
  isAzureAppInjectionKey,
  isCrossPlatformAppInjectionKey,
  isJiraAppInjectionKey,
  isWebAppInjectionKey,
  subAppStateInjectionKey
} from '@/utils/injection-keys'
import {
  getAtlassianIssueUrl,
  getAtlassianIssueUrlForCrossPlatformApp,
  getAtlassianIssueUrlForWebApp
} from '@jira/util'

import AppIcon from '@/components/ui/AppIcon/AppIcon'

defineOptions({
  name: 'PlatformElementViewer'
})

const props = defineProps({
  platformElementId: {
    type: [String, Number],
    required: true
  }
})

const appPlatform = inject(appPlatformInjectionKey)
const isWebApp = inject(isWebAppInjectionKey)
const isCrossPlatformApp = inject(isCrossPlatformAppInjectionKey)
/**
 * @typedef {{ hasAccessToJira: boolean } | null} SubAppState
 */
const subAppState = inject(subAppStateInjectionKey, null)
const isJiraApp = inject(isJiraAppInjectionKey)
const isAzureApp = inject(isAzureAppInjectionKey)

const shouldShowLink = computed(() => {
  if (isAzureApp) {
    return Boolean(AZURE_DEVOPS_SDK)
  }

  return hasAccessToJira.value
})

const linkHref = computed(() => {
  if (isWebApp) {
    return getAtlassianIssueUrlForWebApp(props.platformElementId)
  }

  if (isCrossPlatformApp) {
    return getAtlassianIssueUrlForCrossPlatformApp(props.platformElementId)
  }

  if (isAzureApp) return null

  return getAtlassianIssueUrl(props.platformElementId)
})

const isMockLink = computed(() => {
  return (isJiraApp && isFirefox()) || isAzureApp
})

const { handleExternalLink } = useExternalLinkHelper()

const onLinkClick = async () => {
  if (isMockLink.value) {
    handleExternalLink({
      id: props.platformElementId,
      source: EXTERNAL_LINK_HANDLER_SOURCES.PLATFORM_ELEMENT_LINK,
      appPlatform
    })
  }
}

// const viewIssue = () => {
//   const viewIssueModal = new ViewIssueModal({
//     onClose: () => {
//       console.log('ViewIssueModal closed')
//     },
//     context: {
//       issueKey: props.platformElementId
//     }
//   })
//
//   viewIssueModal.open()
// }

const store = useStore()

const hasAccessToJira = computed(() => {
  if (subAppState) {
    return subAppState.hasAccessToJira || false
  }

  return store.state.system.userData.hasAccessToJira || false
})
</script>

<style lang="scss" scoped>
.pev-Link {
  height: 24px;

  display: inline-flex;
  align-items: center;
  color: $primary-color-next;
  font-style: normal;
  font-weight: fw('regular');
  font-family: $system-ui;
  font-size: $fs-14;
  line-height: 20px;
  gap: 8px;
  cursor: pointer;

  @media (any-hover: hover) {
    &:hover {
      text-decoration: underline;
    }
  }
}
</style>
